import { Link } from "react-router-dom";
import { ArrowRight, Hammer, Compass, ShieldCheck, Users } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import Seo from "../components/Seo";
import { Reveal, FadeIn } from "../components/Motion";

const VALUES = [
  {
    icon: Compass,
    title: "Designed on-site",
    body: "We walk your yard before we draw a thing. Every deck is sized to your home, your slope, and the way you use the outdoors.",
  },
  {
    icon: Hammer,
    title: "Built by our own crew",
    body: "No mystery subs. The same hands you meet on day one are the ones swinging hammers through finish day.",
  },
  {
    icon: ShieldCheck,
    title: "Licensed & insured",
    body: "Permits pulled, codes followed, work guaranteed. You sleep easy from kickoff to handoff.",
  },
  {
    icon: Users,
    title: "Local & accountable",
    body: "We're based in Georgia and most of our work comes from referrals across Metro Atlanta. We work hard to keep it that way.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-28">
      <Seo
        title="About Zarco's Decks | Family-Owned Deck Builder — North Georgia"
        description="Zarco's Decks & Outdoor Living was founded by Orlando Zarco — built on hands-on craftsmanship and family values, serving Rome, Cartersville and North Georgia."
      />
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <FadeIn className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.2em] text-[#99582A] font-semibold">
              About Zarco's Decks
            </p>
            <h1 className="font-display text-5xl md:text-6xl tracking-tighter font-medium mt-5 leading-[1.02]">
              Built on experience.<br />Built by family.
            </h1>
            <p className="mt-8 text-[#4A4A4A] text-base md:text-lg leading-relaxed max-w-xl">
              Zarco's Decks was founded by Orlando Zarco on a simple belief:
              build every project with the same care, strength, and attention to
              detail you would expect at your own home. What began with years of
              hands-on craftsmanship has grown into a family-built company
              creating decks and outdoor living spaces for homeowners across
              North Georgia. That foundation still guides every project we take
              on today.
            </p>
            <Link
              to="/contact"
              data-testid="about-free-estimate-btn"
              className="inline-flex items-center gap-3 mt-10 bg-[#111] text-white hover:bg-[#99582A] transition-colors px-8 py-4 font-semibold tracking-wide"
            >
              Get a Free Estimate <ArrowRight size={18} />
            </Link>
          </FadeIn>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="img-hover img-spotlight aspect-[4/5] w-full" data-testid="about-team-image">
              <img
                src="/about-orlando-framing-1400.webp"
                srcSet="/about-orlando-framing-640.webp 640w, /about-orlando-framing-1000.webp 1000w, /about-orlando-framing-1400.webp 1400w"
                sizes="(min-width: 1024px) 50vw, 100vw"
                alt="Orlando Zarco working hands-on on elevated deck framing at a North Georgia home"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
                style={{ objectPosition: "center 45%" }}
              />
            </div>
            <p className="mt-3 text-xs text-[#666] italic">
              Orlando Zarco on the framing — hands-on from day one
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#FAF8F4] px-6 md:px-12 lg:px-20 py-24">
        <SectionHeading
          overline="How We Work"
          title="Old-school craftsmanship. Modern accountability."
        />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map(({ icon: Icon, title, body }, i) => (
            <Reveal
              key={title}
              delay={i * 0.08}
              data-testid={`about-value-${title.toLowerCase().replace(/\s+/g, "-")}`}
              className="bg-white border border-[#E5E5E5] p-8 hover:border-[#99582A] transition-colors"
            >
              <Icon size={32} strokeWidth={1.5} className="text-[#99582A]" />
              <h3 className="font-display text-xl tracking-tight font-medium mt-5">
                {title}
              </h3>
              <p className="text-sm text-[#666] leading-relaxed mt-3">{body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-24">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              overline="The Process"
              title="Four steps. Zero surprises."
            />
          </div>
          <div className="lg:col-span-7 space-y-10">
            {[
              { n: "01", t: "On-site walk-through", d: "We meet at your home, listen to how you'll use the space, measure, and talk materials." },
              { n: "02", t: "Detailed written quote", d: "Line-item pricing. Real timeline. Everything in writing before you sign anything." },
              { n: "03", t: "Permits & build", d: "We pull permits, schedule inspections, and keep the jobsite clean while we build." },
              { n: "04", t: "Final walk & warranty", d: "We hand the deck over with a workmanship warranty and clear care instructions." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08} y={20} className="grid grid-cols-12 gap-6 border-b border-[#E5E5E5] pb-8">
                <p className="col-span-2 font-display text-3xl text-[#99582A]">{s.n}</p>
                <div className="col-span-10">
                  <p className="font-display text-2xl tracking-tight font-medium">{s.t}</p>
                  <p className="text-[#666] mt-2 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
